// src/pages/task/hooks/useTask.ts
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useInterval from '@use-it/interval'
import { getSalesApi } from '../../../salesApi/salesApi'
import { TaskStatus } from '../../../common/types/enums'
import { selectCurrentUser } from '../../../store/slices/currentUserSlice'
import { selectExecutorClusters } from '../../../store/slices/executorSlice'
import { ClusterInterface } from '../../../common/types/interfaces'
import {
  selectTaskPool,
  selectMyTasks,
  selectTaskLoading,
  selectTaskRefreshing,
  selectTaskLoadingMore,
  selectTaskError,
  selectHasMorePool,
  selectHasMoreMy,
  selectOffsetPool,
  selectOffsetMy,
  setPool,
  appendPool,
  setMy,
  appendMy,
  setLoading,
  setRefreshing,
  setLoadingMore,
  setError,
  upsertTask,
  resetTasks,
} from '../../../store/slices/taskSlice'

export enum TaskFilter {
  POOL = 'pool',
  MY = 'my',
}

const PAGE_SIZE = 20
const POLLING_INTERVAL = 15000

export const useTask = () => {
  const dispatch = useDispatch()
  const salesApi = getSalesApi()
  const currentUser = useSelector(selectCurrentUser)
  const executorClusters = useSelector(selectExecutorClusters)

  const pool = useSelector(selectTaskPool)
  const my = useSelector(selectMyTasks)
  const loading = useSelector(selectTaskLoading)
  const refreshing = useSelector(selectTaskRefreshing)
  const loadingMore = useSelector(selectTaskLoadingMore)
  const error = useSelector(selectTaskError)
  const hasMorePool = useSelector(selectHasMorePool)
  const hasMoreMy = useSelector(selectHasMoreMy)
  const offsetPool = useSelector(selectOffsetPool)
  const offsetMy = useSelector(selectOffsetMy)

  const [filter, setFilter] = useState<TaskFilter>(TaskFilter.POOL)
  const [selectedClusterId, setSelectedClusterId] = useState<number | null>(null)
  const [statusFilter, setStatusFilter] = useState<TaskStatus | null>(null)

  const tasks = filter === TaskFilter.POOL ? pool : my
  const hasMore = filter === TaskFilter.POOL ? hasMorePool : hasMoreMy
  const offset = filter === TaskFilter.POOL ? offsetPool : offsetMy

  const handleFilterChange = useCallback((newFilter: TaskFilter) => {
    setFilter(newFilter)
    setStatusFilter(null)
  }, [])

  const fetchFromClusters = useCallback(
    async (offsetValue: number) => {
      const clusters = selectedClusterId
        ? executorClusters.filter((c) => c.cluster.id === selectedClusterId)
        : executorClusters

      if (!clusters.length) return []

      const results = await Promise.all(
        clusters.map(async (executorCluster) => {
          try {
            salesApi.setClusterUrl(executorCluster.cluster)

            const params: { limit: number; offset: number; status?: number } = {
              limit: PAGE_SIZE,
              offset: offsetValue,
            }

            if (filter === TaskFilter.MY && statusFilter !== null) {
              params.status = statusFilter
            }

            const response =
              filter === TaskFilter.POOL
                ? await salesApi.getTaskPool(params)
                : await salesApi.getMyTasks(params)

            // ❌ БЕЗ { ...task, cluster } — не пересоздаём объекты
            return (response?.data || []).map((task) => task)
          } catch (err) {
            console.error(`Failed to fetch tasks for cluster ${executorCluster.cluster.id}:`, err)
            return []
          }
        }),
      )

      return results.flat().sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime()
        const dateB = new Date(b.createdAt).getTime()
        return dateB - dateA
      })
    },
    [executorClusters, filter, selectedClusterId, statusFilter, salesApi],
  )

  const fetchTasks = useCallback(
    async (silent = false) => {
      if (!currentUser?.id) {
        dispatch(resetTasks())
        return
      }

      if (!executorClusters?.length) {
        return
      }

      const hasData = (filter === TaskFilter.POOL ? pool : my).length > 0

      if (!silent && !hasData) dispatch(setLoading(true))
      // ❌ убрали silent refreshing
      dispatch(setError(null))

      try {
        const newTasks = await fetchFromClusters(0)

        if (filter === TaskFilter.POOL) {
          dispatch(setPool(newTasks))
        } else {
          dispatch(setMy(newTasks))
        }
      } catch (err) {
        console.error('Fetch tasks error:', err)
        dispatch(setError('Не удалось загрузить задания'))
      } finally {
        dispatch(setLoading(false))
      }
    },
    [currentUser?.id, executorClusters, filter, fetchFromClusters, pool, my, dispatch],
  )

  const loadMore = useCallback(async () => {
    if (loadingMore || loading || refreshing || !hasMore) return

    dispatch(setLoadingMore(true))

    try {
      const newTasks = await fetchFromClusters(offset)

      if (filter === TaskFilter.POOL) {
        dispatch(appendPool(newTasks))
      } else {
        dispatch(appendMy(newTasks))
      }
    } catch (err) {
      console.error('Load more error:', err)
    } finally {
      dispatch(setLoadingMore(false))
    }
  }, [loadingMore, loading, refreshing, hasMore, offset, filter, fetchFromClusters, dispatch])

  // ❌ явный refresh — здесь refreshing
  const refresh = useCallback(async () => {
    dispatch(setRefreshing(true))
    try {
      await fetchTasks(true)
    } finally {
      dispatch(setRefreshing(false))
    }
  }, [fetchTasks, dispatch])

  const takeTask = useCallback(
    async (taskId: number, cluster: ClusterInterface) => {
      try {
        salesApi.setClusterUrl(cluster)
        const result = await salesApi.takeTask(taskId)

        if (result?.task) {
          dispatch(upsertTask(result.task))
        }

        await fetchTasks(true)
      } catch (err) {
        console.error('Take task error:', err)
      }
    },
    [salesApi, dispatch, fetchTasks],
  )

  const updateStatus = useCallback(
    async (taskId: number, status: TaskStatus, cluster: ClusterInterface, comment?: string) => {
      try {
        salesApi.setClusterUrl(cluster)
        const result = await salesApi.updateTaskStatus(taskId, status, comment)

        if (result?.task) {
          dispatch(upsertTask(result.task))
        }

        await fetchTasks(true)
      } catch (err) {
        console.error('Update task status error:', err)
      }
    },
    [salesApi, dispatch, fetchTasks],
  )

  useEffect(() => {
    fetchTasks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, selectedClusterId, statusFilter, currentUser?.id, executorClusters.length])

  useInterval(
    () => {
      if (currentUser?.id && !loading && !refreshing && !loadingMore) {
        fetchTasks(true)
      }
    },
    currentUser?.id ? POLLING_INTERVAL : null,
  )

  return {
    tasks,
    pool,
    my,
    loading,
    refreshing,
    loadingMore,
    hasMore,
    error,
    filter,
    setFilter: handleFilterChange,
    selectedClusterId,
    setSelectedClusterId,
    statusFilter,
    setStatusFilter,
    executorClusters,
    refresh,
    loadMore,
    takeTask,
    updateStatus,
    fetchTasks,
  }
}
